import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { Upload, X, FileText, Image, Video, File, Eye, EyeOff, Trash2, Plus, Globe, Music, Download, ExternalLink, Pencil, Check, Copy } from 'lucide-react';
import { talentMedia, libraries } from '../../services/apiService';
import { Autocomplete } from '@anyemp/ui-kit';
import { mediaTypes as mediaTypesData } from '../../utils/mediaTypeUtils';
import ConfirmDialog from './ConfirmDialog';

const FileUpload = forwardRef(({
  talentId,
  onFileUploaded,
  onFileDeleted,
  onFileUpdated,
  existingFiles = [],
  maxFiles = 10,
  allowedTypes = ['image/*', 'application/pdf', 'video/*'],
  maxSize = 50 * 1024 * 1024, // 50MB
  canCreate = true,
  canUpdate = true,
  canDelete = true,
  allowedMediaTypes = null, // null = всі типи; масив значень = фільтр (read/загальний)
  // Опціональні пер-action списки. Якщо не передано — використовується allowedMediaTypes.
  allowedCreateTypes = null,
  allowedUpdateTypes = null,
  allowedDeleteTypes = null,
  showAddButton = true
}, ref) => {
  // Резолвимо ефективні списки з fallback на allowedMediaTypes
  const _createTypes = allowedCreateTypes ?? allowedMediaTypes;
  const _updateTypes = allowedUpdateTypes ?? allowedMediaTypes;
  const _deleteTypes = allowedDeleteTypes ?? allowedMediaTypes;
  const _canUpdateType = (t) => canUpdate && (!_updateTypes || _updateTypes.includes(t));
  const _canDeleteType = (t) => canDelete && (!_deleteTypes || _deleteTypes.includes(t));
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showMediaTypeSelector, setShowMediaTypeSelector] = useState(false);
  const [selectedMediaType, setSelectedMediaType] = useState(null);
  const [selectedLanguageId, setSelectedLanguageId] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [languagesOptions, setLanguagesOptions] = useState([]);
  const [languagesLoaded, setLanguagesLoaded] = useState(false);
  const [loadingLanguages, setLoadingLanguages] = useState(false);
  const [editingFileId, setEditingFileId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [newMediaPublicVisible, setNewMediaPublicVisible] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // file object to delete
  const fileInputRef = useRef(null);

  // Маппинг иконок для типов медиа
  const mediaTypeIcons = {
    photo: Image,
    portrait: Image,
    resume: FileText,
    portfolio: File,
    youtube: Video,
    youtube_shorts: Video,
    contract: FileText,
    audio: Music,
    interview: Video
  };

  // Типы медиа-файлов с иконками (фільтруємо якщо задано allowedMediaTypes)
  const mediaTypes = mediaTypesData
    .filter(type => !allowedMediaTypes || allowedMediaTypes.includes(type.value))
    .map(type => ({
      ...type,
      icon: mediaTypeIcons[type.value] || File
    }));

  // Функция для загрузки языков из API
  const loadLanguages = async () => {
    if (languagesLoaded || loadingLanguages) {
      return; // Если уже загружены или идет загрузка, не делаем повторный запрос
    }

    setLoadingLanguages(true);
    try {
      const response = await libraries.getLanguages();
      if (response.success && response.data) {
        setLanguagesOptions(response.data);
        setLanguagesLoaded(true);
      }
    } catch (error) {
      console.error('Error fetching languages:', error);
    } finally {
      setLoadingLanguages(false);
    }
  };

  useImperativeHandle(ref, () => ({
    openUpload: async () => {
      await loadLanguages();
      setShowMediaTypeSelector(true);
    }
  }));

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = async (files) => {
    const fileArray = Array.from(files);

    // Проверяем количество файлов
    if (existingFiles.length + fileArray.length > maxFiles) {
      alert(`Maximum number of files: ${maxFiles}`);
      return;
    }

    // Проверяем каждый файл
    for (const file of fileArray) {
      if (!isFileTypeAllowed(file)) {
        alert(`Unsupported file type: ${file.name}`);
        return;
      }

      if (file.size > maxSize) {
        alert(`File too large: ${file.name} (maximum ${formatFileSize(maxSize)})`);
        return;
      }
    }

    // Загружаем файлы
    for (const file of fileArray) {
      await uploadFile(file);
    }
  };

  const isFileTypeAllowed = (file) => {
    return allowedTypes.some(type => {
      if (type.endsWith('/*')) {
        return file.type.startsWith(type.slice(0, -1));
      }
      return file.type === type;
    });
  };

  const uploadFile = async (file) => {
    // Если talentId отсутствует, сохраняем файл в состоянии для последующей загрузки
    if (!talentId) {
      const tempFileData = createFileMediaData(file, true);

      if (onFileUploaded) {
        onFileUploaded(tempFileData);
      }

      // Сбрасываем состояние после добавления файла
      setSelectedMediaType(null);
      setSelectedLanguageId('');
      setNewMediaPublicVisible(true);
      return;
    }

    // Если talentId есть, загружаем файл немедленно
    setUploading(true);
    setUploadProgress(0);

    try {
      const result = await talentMedia.uploadMedia(
        talentId,
        file,
        selectedMediaType,
        newMediaPublicVisible,
        selectedLanguageId || null
      );

      if (result.success) {
        if (onFileUploaded) {
          onFileUploaded(result.data);
        }
        setUploadProgress(100);
        // Сбрасываем состояние после успешной загрузки
        setSelectedMediaType(null);
        setSelectedLanguageId('');
        setNewMediaPublicVisible(true);
      } else {
        throw new Error('File upload error');
      }
    } catch (error) {
      console.error('❌ Error uploading file:', error);
      alert(`Error uploading file ${file.name}: ${error.message}`);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (mimeType) => {
    if (!mimeType) return <File className="w-5 h-5" />;
    if (mimeType.startsWith('image/')) return <Image className="w-5 h-5" />;
    if (mimeType.startsWith('video/')) return <Video className="w-5 h-5" />;
    if (mimeType === 'application/pdf') return <FileText className="w-5 h-5" />;
    if (mimeType === 'text/url') return <ExternalLink className="w-5 h-5" />;
    return <File className="w-5 h-5" />;
  };

  const getFileUrl = (file) => {
    if (file.file_url) return file.file_url;
    if (file.file_path && file.file_path.startsWith('http')) return file.file_path;
    if (file.file_path && file.file_path.startsWith('/uploads')) return file.file_path;
    if (file.is_temp && file.file) return URL.createObjectURL(file.file);
    return null;
  };

  const handleOpenFile = (file) => {
    const url = getFileUrl(file);
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadFile = (file) => {
    const url = getFileUrl(file);
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = file.file_name || 'download';
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const startEditing = async (file) => {
    // Загружаем языки при открытии формы редактирования
    await loadLanguages();

    setEditingFileId(file.id);
    setEditData({
      media_type: file.media_type || '',
      language_id: file.language_id ? file.language_id.toString() : '',
      file_path: file.mime_type === 'text/url' ? (file.file_path || '') : '',
    });
  };

  const cancelEditing = () => {
    setEditingFileId(null);
    setEditData({});
  };

  const saveEditing = async (fileId) => {
    const updatePayload = {
      media_type: editData.media_type,
      language_id: editData.language_id ? parseInt(editData.language_id) : null,
    };

    // Для URL-записей обновляем также file_path
    const file = existingFiles.find(f => f.id === fileId);
    if (file && file.mime_type === 'text/url' && editData.file_path) {
      updatePayload.file_path = editData.file_path;
      updatePayload.file_name = editData.file_path.split('/').pop() || 'URL Link';
    }

    // Временные файлы — обновляем локально
    if (typeof fileId === 'string' && (fileId.startsWith('temp-') || fileId.startsWith('temp-url-'))) {
      if (onFileUpdated) {
        onFileUpdated({ ...file, ...updatePayload });
      }
      setEditingFileId(null);
      setEditData({});
      return;
    }

    if (!talentId) {
      if (onFileUpdated && file) {
        onFileUpdated({ ...file, ...updatePayload });
      }
      setEditingFileId(null);
      setEditData({});
      return;
    }

    setSaving(true);
    try {
      const result = await talentMedia.updateMedia(talentId, fileId, updatePayload);
      if (result.success && onFileUpdated) {
        onFileUpdated(result.data);
      }
      setEditingFileId(null);
      setEditData({});
    } catch (error) {
      console.error('Error updating media:', error);
      alert(`Error updating file: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Функция для создания структуры файловых медиа-данных
  const createFileMediaData = (file, isTemporary = false) => {
    const baseData = {
      file_name: file.name,
      file_size: file.size,
      mime_type: file.type,
      media_type: selectedMediaType,
      language_id: selectedLanguageId || null,
      public_visible: newMediaPublicVisible
    };

    if (isTemporary) {
      return {
        ...baseData,
        id: `temp-${Date.now()}-${Math.random()}`,
        file: file, // Сохраняем сам файл
        is_temp: true
      };
    }

    // Для не временных файлов файл загружается через API, поэтому не включаем его в структуру
    return baseData;
  };

  // Функция для создания структуры URL медиа-данных
  const createUrlMediaData = (url, isTemporary = false) => {
    const baseData = {
      media_type: selectedMediaType,
      file_path: url,
      file_name: url.split('/').pop() || 'URL Link',
      file_size: 0,
      mime_type: 'text/url',
      public_visible: newMediaPublicVisible,
      language_id: selectedLanguageId || null
    };

    if (isTemporary) {
      return {
        ...baseData,
        id: `temp-url-${Date.now()}-${Math.random()}`,
        is_temp: true
      };
    }

    return {
      ...baseData,
      talent_id: talentId
    };
  };

  const deleteFile = (file) => {
    setDeleteConfirm(file);
  };

  const confirmDeleteFile = async () => {
    const fileId = deleteConfirm.id;

    // Temporary file (no talentId) — just remove from list
    if (typeof fileId === 'string' && fileId.startsWith('temp-')) {
      if (onFileDeleted) onFileDeleted(fileId);
      return;
    }

    // No talentId — remove from list only
    if (!talentId) {
      if (onFileDeleted) onFileDeleted(fileId);
      return;
    }

    try {
      const result = await talentMedia.deleteMedia(talentId, fileId);
      if (result.success) {
        onFileDeleted(fileId);
      } else {
        throw new Error('File deletion error');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      alert(`Error deleting file: ${error.message}`);
    }
  };

  const addUrlMedia = async () => {
    if (!urlInput.trim()) {
      alert('Please enter a URL');
      return;
    }

    // Простая валидация URL
    try {
      new URL(urlInput);
    } catch {
      alert('Please enter a valid URL');
      return;
    }

    // Если talentId отсутствует, сохраняем URL в состоянии для последующей загрузки
    if (!talentId) {
      const urlMediaData = createUrlMediaData(urlInput, true);

      if (onFileUploaded) {
        onFileUploaded(urlMediaData);
      }
      setUrlInput('');
      setSelectedMediaType(null);
      setSelectedLanguageId('');
      setNewMediaPublicVisible(true);
      return;
    }

    setUploading(true);

    try {
      // Создаем объект медиа-файла для URL
      const urlMediaData = createUrlMediaData(urlInput, false);

      // Используем метод создания медиа-записи напрямую
      const result = await talentMedia.createMedia(urlMediaData);

      if (result.success) {
        if (onFileUploaded) {
          onFileUploaded(result.data);
        }
        setUrlInput('');
        setSelectedMediaType(null);
        setSelectedLanguageId('');
        setNewMediaPublicVisible(true);
      } else {
        throw new Error('Error adding URL');
      }
    } catch (error) {
      console.error('❌ Error adding URL media:', error);
      alert(`Error adding URL: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const toggleVisibility = async (fileId, currentVisibility) => {
    // Если это временный файл (без talentId), просто обновляем в состоянии
    if (typeof fileId === 'string' && (fileId.startsWith('temp-') || fileId.startsWith('temp-url-'))) {
      if (onFileUpdated) {
        // Находим файл в existingFiles и обновляем его видимость
        const file = existingFiles.find(f => f.id === fileId);
        if (file) {
          const updatedFile = { ...file, public_visible: !currentVisibility };
          onFileUpdated(updatedFile);
        }
      }
      return;
    }

    // Если talentId отсутствует, просто обновляем в состоянии
    if (!talentId) {
      if (onFileUpdated) {
        const file = existingFiles.find(f => f.id === fileId);
        if (file) {
          const updatedFile = { ...file, public_visible: !currentVisibility };
          onFileUpdated(updatedFile);
        }
      }
      return;
    }

    try {
      const result = await talentMedia.updateMedia(
        talentId,
        fileId,
        { public_visible: !currentVisibility }
      );

      if (result.success && onFileUpdated) {
        onFileUpdated(result.data);
      } else {
        throw new Error('Error updating file visibility');
      }
    } catch (error) {
      console.error('Error updating file visibility:', error);
      alert(`Error updating file visibility: ${error.message}`);
    }
  };

  return (
    <>
    <div className="space-y-4">
      {/* Кнопка добавления медиа */}
      {canCreate && showAddButton && !showMediaTypeSelector && !selectedMediaType && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={async () => {
              await loadLanguages(); // Загружаем языки при открытии формы
              setShowMediaTypeSelector(true);
            }}
            disabled={uploading || existingFiles.length >= maxFiles}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Add Media</span>
          </button>
        </div>
      )}

      {/* Селектор типа медиа */}
      {showMediaTypeSelector && (
        <div className="bg-light-bg-primary dark:bg-dark-bg-primary border border-light-border-secondary dark:border-dark-border-secondary rounded-lg p-6">
          <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary mb-4">Select Media Type</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {mediaTypes.filter(t => !_createTypes || _createTypes.includes(t.value)).map((type) => {
              const IconComponent = type.icon;
              return (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => {
                    setSelectedMediaType(type.value);
                    setShowMediaTypeSelector(false);
                  }}
                  className="flex flex-col items-center p-4 border border-light-border-secondary dark:border-dark-border-secondary rounded-lg hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                >
                  <IconComponent className="w-8 h-8 text-light-text-secondary dark:text-dark-text-secondary mb-2" />
                  <span className="text-sm text-light-text-primary dark:text-dark-text-primary text-center">{type.label}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={() => {
                setShowMediaTypeSelector(false);
                setSelectedMediaType(null);
              }}
              className="px-4 py-2 text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Область добавления медиа */}
      {!showMediaTypeSelector && selectedMediaType && (
        <div className="bg-light-bg-primary dark:bg-dark-bg-primary border border-light-border-secondary dark:border-dark-border-secondary rounded-lg p-6">
          <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary mb-4">
            {mediaTypes.find(t => t.value === selectedMediaType)?.label}
          </h3>

          {/* Скрытый input для файлов */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={allowedTypes.join(',')}
            onChange={handleChange}
            className="hidden"
          />

          {/* Drag & Drop зона + кнопка выбора файла */}
          <div
            className={`mb-6 flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-lg transition-colors cursor-pointer ${
              dragActive
                ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/20'
                : 'border-light-border-secondary dark:border-dark-border-secondary hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20'
            } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
          >
            <Upload className={`w-10 h-10 mb-3 ${dragActive ? 'text-blue-500 dark:text-blue-400' : 'text-light-text-secondary dark:text-dark-text-secondary'}`} />
            <p className="text-light-text-primary dark:text-dark-text-primary font-medium mb-1">
              {dragActive ? 'Drop files here' : 'Drag & drop files here'}
            </p>
            <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">
              or <span className="text-blue-600 dark:text-blue-400 underline">browse from your computer</span>
            </p>
            <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mt-2">
              Max size: {formatFileSize(maxSize)}
            </p>
            {uploading && (
              <div className="w-full mt-4">
                <div className="w-full bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mt-1 text-center">
                  Uploading... {uploadProgress}%
                </p>
              </div>
            )}
          </div>

          {/* Поле ввода URL */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
              Or enter a link
            </label>
            <input
              type="url"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://example.com"
              disabled={uploading}
              className="w-full px-3 py-2 border border-light-border-secondary dark:border-dark-border-secondary rounded-lg shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-light-bg-primary dark:bg-dark-bg-primary text-light-text-primary dark:text-dark-text-primary placeholder-light-text-tertiary dark:placeholder-dark-text-tertiary disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary mt-2">
              You can specify a link or upload a file above
            </p>
          </div>

          {/* Выбор языка */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
              <Globe className="w-4 h-4 inline mr-2" />
              Language (optional)
            </label>
            <div className="flex space-x-3">
              <div className="flex-1">
                <Autocomplete
                  clearable
                  fullWidth
                  value={selectedLanguageId}
                  onChange={(val) => setSelectedLanguageId(val || '')}
                  placeholder="Select language for subtitles"
                  options={languagesOptions.map((lang) => ({
                    value: lang.id.toString(),
                    label: lang.name
                  }))}
                />
              </div>
              <button
                type="button"
                onClick={() => setNewMediaPublicVisible(prev => !prev)}
                className={`px-3 py-2 rounded-lg border flex items-center space-x-1.5 transition-colors flex-shrink-0 ${
                  newMediaPublicVisible
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                    : 'border-light-border-secondary dark:border-dark-border-secondary text-light-text-secondary dark:text-dark-text-secondary hover:border-light-border-primary dark:hover:border-dark-border-primary'
                }`}
                title={newMediaPublicVisible ? 'Will be public — click to make private' : 'Will be private — click to make public'}
              >
                {newMediaPublicVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                <span className="text-sm font-medium">Public</span>
              </button>
              <button
                type="button"
                onClick={addUrlMedia}
                disabled={uploading || !urlInput.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? 'Adding...' : 'Add'}
              </button>
            </div>
            <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary mt-1">
              Select language for subtitles or localized content
            </p>
          </div>

          {/* Кнопка возврата */}
          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={() => {
                setSelectedMediaType(null);
                setUrlInput('');
                setShowMediaTypeSelector(true);
              }}
              className="px-4 py-2 text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary"
            >
              Back to type selection
            </button>
          </div>
        </div>
      )}

      {/* Список загруженных файлов, сгруппированных по типу */}
      {existingFiles.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary">Uploaded Files</h3>
          {(() => {
            const orderedTypes = mediaTypes.map(t => t.value);
            const groupedFiles = existingFiles.reduce((acc, file) => {
              const type = file.media_type || 'other';
              if (!acc[type]) acc[type] = [];
              acc[type].push(file);
              return acc;
            }, {});
            const sortedTypes = [
              ...orderedTypes.filter(t => groupedFiles[t]),
              ...Object.keys(groupedFiles).filter(t => !orderedTypes.includes(t))
            ];
            return sortedTypes.map(type => {
              const files = groupedFiles[type];
              const typeInfo = mediaTypes.find(t => t.value === type);
              const Icon = typeInfo?.icon || File;
              const label = typeInfo?.label || type;
              return (
                <div key={type} className="space-y-2">
                  <h4 className="flex items-center gap-2 text-sm font-semibold text-light-text-secondary dark:text-dark-text-secondary border-b border-light-border-primary dark:border-dark-border-primary pb-1">
                    <Icon className="w-4 h-4" />
                    {label}
                    <span className="text-xs font-normal text-light-text-tertiary dark:text-dark-text-tertiary">({files.length})</span>
                  </h4>
                  <div className="space-y-2">
                    {files.map((file) => (
              <div
                key={file.id}
                className="p-3 bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-lg border border-light-border-primary dark:border-dark-border-primary"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                    <div className="text-light-text-secondary dark:text-dark-text-secondary flex-shrink-0">
                      {getFileIcon(file.mime_type)}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-light-text-primary dark:text-dark-text-primary truncate">
                        {getFileUrl(file) ? (
                          <button
                            type="button"
                            onClick={() => handleOpenFile(file)}
                            className="text-blue-600 dark:text-blue-400 hover:underline text-left truncate"
                            title={`Open ${file.file_name}`}
                          >
                            {file.file_name}
                          </button>
                        ) : (
                          file.file_name
                        )}
                        {file.is_temp && (
                          <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200">
                            Pending upload
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">
                        {file.formatted_file_size || formatFileSize(file.file_size || 0)} • {file.media_type}
                        {file.language && (
                          <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                            <Globe className="w-3 h-3 mr-1" />
                            {file.language.name}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 flex-shrink-0">
                    {getFileUrl(file) && (
                      file.mime_type === 'text/url' ? (
                        <button
                          type="button"
                          onClick={() => handleOpenFile(file)}
                          className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md"
                          title="Open link"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleDownloadFile(file)}
                          className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md"
                          title="Download file"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      )
                    )}

                    {getFileUrl(file) && (
                      <button
                        type="button"
                        onClick={() => {
                          const url = getFileUrl(file);
                          if (url) {
                            navigator.clipboard.writeText(
                              url.startsWith('http') ? url : `${window.location.origin}${url}`
                            );
                          }
                        }}
                        className="p-2 text-light-text-tertiary dark:text-dark-text-tertiary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary rounded-md"
                        title="Copy link"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    )}

                    {_canUpdateType(file.media_type) && (
                      <button
                        type="button"
                        onClick={() => editingFileId === file.id ? cancelEditing() : startEditing(file)}
                        className={`p-2 rounded-md ${
                          editingFileId === file.id
                            ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                            : 'text-light-text-tertiary dark:text-dark-text-tertiary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary'
                        }`}
                        title={editingFileId === file.id ? 'Cancel editing' : 'Edit'}
                      >
                        {editingFileId === file.id ? <X className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
                      </button>
                    )}

                    {_canUpdateType(file.media_type) && (
                      <button
                        type="button"
                        onClick={() => toggleVisibility(file.id, file.public_visible)}
                        className={`p-2 rounded-md ${
                          file.public_visible
                            ? 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
                            : 'text-light-text-tertiary dark:text-dark-text-tertiary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary'
                        }`}
                        title={file.public_visible ? 'Hide from public access' : 'Show publicly'}
                      >
                        {file.public_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    )}

                    {_canDeleteType(file.media_type) && (
                      <button
                        type="button"
                        onClick={() => deleteFile(file)}
                        className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md"
                        title="Delete file"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Inline-форма редактирования */}
                {editingFileId === file.id && (
                  <div className="mt-3 pt-3 border-t border-light-border-primary dark:border-dark-border-primary space-y-3">
                    {/* Тип медиа */}
                    <div>
                      <label className="block text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">
                        Media Type
                      </label>
                      <Autocomplete
                        clearable
                        fullWidth
                        value={editData.media_type}
                        onChange={(val) => setEditData(prev => ({ ...prev, media_type: val || '' }))}
                        placeholder="Select type"
                        options={mediaTypes.filter(t => !_updateTypes || _updateTypes.includes(t.value)).map(t => ({ value: t.value, label: t.label }))}
                      />
                    </div>

                    {/* Язык */}
                    <div>
                      <label className="block text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">
                        <Globe className="w-3 h-3 inline mr-1" />
                        Language
                      </label>
                      <Autocomplete
                        clearable
                        fullWidth
                        value={editData.language_id}
                        onChange={(val) => setEditData(prev => ({ ...prev, language_id: val || '' }))}
                        placeholder="Select language"
                        options={languagesOptions.map(lang => ({
                          value: lang.id.toString(),
                          label: lang.name
                        }))}
                      />
                    </div>

                    {/* URL (только для text/url) */}
                    {file.mime_type === 'text/url' && (
                      <div>
                        <label className="block text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">
                          URL
                        </label>
                        <input
                          type="url"
                          value={editData.file_path}
                          onChange={(e) => setEditData(prev => ({ ...prev, file_path: e.target.value }))}
                          placeholder="https://example.com"
                          className="w-full px-3 py-2 text-sm border border-light-border-secondary dark:border-dark-border-secondary rounded-lg bg-light-bg-primary dark:bg-dark-bg-primary text-light-text-primary dark:text-dark-text-primary placeholder-light-text-tertiary dark:placeholder-dark-text-tertiary focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    )}

                    {/* Кнопки */}
                    <div className="flex justify-end space-x-2">
                      <button
                        type="button"
                        onClick={cancelEditing}
                        className="px-3 py-1.5 text-sm text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary rounded-md"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => saveEditing(file.id)}
                        disabled={saving}
                        className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                      >
                        <Check className="w-3.5 h-3.5" />
                        <span>{saving ? 'Saving...' : 'Save'}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
                    ))}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}
    </div>
    <ConfirmDialog
      isOpen={!!deleteConfirm}
      onClose={() => setDeleteConfirm(null)}
      onConfirm={confirmDeleteFile}
      confirmStyle="danger"
      title="Delete File"
      message={deleteConfirm
        ? `Are you sure you want to delete "${deleteConfirm.original_name || deleteConfirm.file_name || 'this file'}"? This action cannot be undone.`
        : ''}
      confirmText="Delete"
    />
    </>
  );
});

FileUpload.displayName = 'FileUpload';

export default FileUpload;
